import { Platform } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import { getLogger } from '@/utils/logger';
import { globalState } from '@/state/globalState';
import { APP_CONSTANTS } from '@/config/constants';
import {
  getBufferedLogEvents,
  getBufferedLogSessionId,
  isLogBufferEnabled,
} from '@/utils/logBuffer';

const logger = getLogger('CHECKLIST-LOG-BUFFER');
const CHECKLIST_LOGS_TABLE = APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_LOGS.NAME;

interface LocalUriContext {
  checklistId: string;
  empresaId?: string;
  campos: { campo_id: string; valor: string }[];
}

const sentKeys = new Set<string>();

const buildDeviceInfo = () => ({
  platform: Platform.OS,
  version: Platform.Version ?? 'unknown',
});

type ChecklistLogPayload = {
  id: string;
  session_id: string;
  trigger: string;
  checklist_id: string;
  empresa_id: string | null;
  user_id: string | null;
  context: Record<string, any>;
  device_info: Record<string, any>;
  logs: ReturnType<typeof getBufferedLogEvents>;
};

const buildDedupKey = (context: LocalUriContext): string => {
  const campos = context.campos.map(campo => campo.campo_id).join(',');
  return `local_uri_payload:${context.checklistId}:${campos}`;
};

const registerPendingLog = async (payload: ChecklistLogPayload): Promise<boolean> => {
  try {
    const { databaseService } = require('@/services/databaseService') as typeof import('@/services/databaseService');
    await databaseService.registerPendingOperation(
      'create',
      CHECKLIST_LOGS_TABLE,
      payload.id,
      JSON.stringify(payload)
    );
    logger.info(
      `[reportLocalUriPayload] Logs adicionados às pendências para envio futuro (ID ${payload.id}).`
    );
    return true;
  } catch (registerError) {
    logger.error(
      '[reportLocalUriPayload] Falha ao registrar pendência para checklist_logs:',
      registerError
    );
    return false;
  }
};

export const ChecklistLogBufferService = {
  async reportLocalUriPayload(context: LocalUriContext): Promise<void> {
    if (!isLogBufferEnabled()) {
      return;
    }

    const snapshot = getBufferedLogEvents();
    if (snapshot.length === 0) {
      logger.warn('[reportLocalUriPayload] Buffer vazio, nada para enviar.');
      return;
    }

    const dedupKey = buildDedupKey(context);
    if (sentKeys.has(dedupKey)) {
      logger.debug(`[reportLocalUriPayload] Logs já enviados para ${dedupKey}, ignorando.`);
      return;
    }
    sentKeys.add(dedupKey);

    const profile = globalState.user.profile.get();
    const logId = uuidv4();
    const contextPayload = {
      ...context,
      app_version: APP_CONSTANTS.VERSION,
    };
    const payload: ChecklistLogPayload = {
      id: logId,
      session_id: getBufferedLogSessionId(),
      trigger: 'local_uri_payload',
      checklist_id: context.checklistId,
      empresa_id: context.empresaId ?? profile?.empresa_id ?? null,
      user_id: profile?.id ?? null,
      context: contextPayload,
      device_info: buildDeviceInfo(),
      logs: snapshot,
    };

    try {
      const { supabase } = require('@/services/supabase') as typeof import('@/services/supabase');
      const { error } = await supabase.from(CHECKLIST_LOGS_TABLE).insert([payload]);
      if (error) {
        logger.error('[reportLocalUriPayload] Falha ao enviar logs para checklist_logs:', error);
        if (!await registerPendingLog(payload)) {
          sentKeys.delete(dedupKey);
        }
      } else {
        logger.info(
          `[reportLocalUriPayload] Logs enviados com sucesso (${snapshot.length} eventos) para checklist ${context.checklistId}`
        );
      }
    } catch (err) {
      logger.error('[reportLocalUriPayload] Erro inesperado ao enviar logs:', err);
      if (!await registerPendingLog(payload)) {
        sentKeys.delete(dedupKey);
      }
    }
  },
};
